
import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCountry } from '@/contexts/country'; // Updated import path
import TagsInput from '../TagsInput';
import { CivicEducationItem } from '@/types/civic-education';

interface BasicInfoFieldsProps {
  item: CivicEducationItem;
  onFieldChange: (field: keyof CivicEducationItem, value: any) => void;
}

const BasicInfoFields = ({ item, onFieldChange }: BasicInfoFieldsProps) => {
  const { countryCode, setCountry, availableCountries } = useCountry();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="title" className="text-sm font-medium">Title</label>
          <Input
            id="title"
            value={item.title}
            onChange={(e) => onFieldChange('title', e.target.value)}
            required
          />
        </div>
        
        <div className="space-y-2">
          <label htmlFor="status" className="text-sm font-medium">Status</label>
          <Select
            value={item.status}
            onValueChange={(value) => onFieldChange('status', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="description" className="text-sm font-medium">Description</label>
        <Textarea
          id="description"
          value={item.description}
          onChange={(e) => onFieldChange('description', e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="country" className="text-sm font-medium">Country</label>
          <Select
            value={item.country_code || 'global'}
            onValueChange={(value) => {
              // If the value is 'global', set it to null in the item but use empty string for CountryContext
              const countryCodeValue = value === 'global' ? null : value;
              setCountry(value === 'global' ? '' : value);
              onFieldChange('country_code', countryCodeValue);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global</SelectItem>
              {availableCountries.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label htmlFor="date" className="text-sm font-medium">Date</label>
          <Input
            id="date"
            type="date"
            value={item.date}
            onChange={(e) => onFieldChange('date', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Tags</label>
        <TagsInput
          tags={item.tags}
          onChange={(tags) => onFieldChange('tags', tags)}
          placeholder="Add tags..."
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Related Issues</label>
        <TagsInput
          tags={item.related_issues}
          onChange={(issues) => onFieldChange('related_issues', issues)}
          placeholder="Add related issues..."
        />
      </div>
    </div>
  );
};

export default BasicInfoFields;
